import { ChevronRight, Home } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface Crumb {
  readonly label: string;
  readonly to: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  afiliados: 'Afiliados',
  contratos: 'Contratos',
  citas: 'Citas médicas',
  cuotas: 'Cuotas',
  facturacion: 'Facturación',
  recibos: 'Recibos',
  tarjetas: 'Tarjetas',
  papeleria: 'Papelería',
  inventario: 'Inventario',
  bodega: 'Bodega',
  compras: 'Compras',
  reportes: 'Reportes',
  siigo: 'SIIGO',
  admin: 'Administración',
  usuarios: 'Usuarios',
  sucursales: 'Sucursales',
  municipios: 'Municipios',
  especialidades: 'Especialidades',
  equipos: 'Equipos',
  estandares: 'Estándares',
  'tipos-agente': 'Tipos de agente',
  doctores: 'Doctores',
  agentes: 'Agentes',
  proveedores: 'Proveedores',
  tarifas: 'Tarifas',
  perfil: 'Perfil',
  'cambiar-clave': 'Cambiar contraseña',
  nueva: 'Nueva',
  nuevo: 'Nuevo',
};

function humanize(segment: string): string {
  return SEGMENT_LABELS[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1);
}

function buildCrumbs(pathname: string): readonly Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];
  let acc = '';
  for (const seg of segments) {
    acc += `/${seg}`;
    crumbs.push({ label: humanize(seg), to: acc });
  }
  return crumbs;
}

export function Breadcrumbs(): ReactElement {
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);

  return (
    <nav aria-label="Breadcrumb" className="hidden md:block">
      <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <li>
          <Link
            to="/"
            className="flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-muted hover:text-foreground"
            aria-label="Inicio"
          >
            <Home className="h-3.5 w-3.5" />
          </Link>
        </li>
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={crumb.to} className="flex items-center gap-1.5">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" aria-hidden />
              {isLast ? (
                <span className="font-medium text-foreground">{crumb.label}</span>
              ) : (
                <Link
                  to={crumb.to}
                  className="rounded-md px-1.5 py-1 hover:bg-muted hover:text-foreground"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
